import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { translateError, createSuccessMessage, TranslatedError, SuccessMessage } from '../utils/errorTranslations';

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'report';

export interface ReportSection {
  type: 'success' | 'error';
  title: string;
  message: string;
  items?: Array<{ label: string; detail?: string }>;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  details?: string;
  originalError?: string;
  showOriginal?: boolean;
  autoClose?: boolean;
  duration?: number;
  reportSections?: ReportSection[];
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (notification: Omit<Notification, 'id'>) => string;
  showSuccess: (action: string, options?: { message?: string; details?: string; counts?: { success?: number; failed?: number; total?: number } }) => string;
  showError: (error: unknown, context?: string) => string;
  showWarning: (title: string, message: string, details?: string) => string;
  showInfo: (title: string, message: string, details?: string) => string;
  hideNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

let notificationId = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((notification: Omit<Notification, 'id'>): string => {
    const id = `notification-${++notificationId}`;
    const newNotification: Notification = {
      ...notification,
      id,
      autoClose: notification.autoClose ?? (notification.type === 'success'),
      duration: notification.duration ?? 10000, // 10 seconds minimum for better readability
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-close if enabled
    if (newNotification.autoClose) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, newNotification.duration);
    }

    return id;
  }, []);

  const showSuccess = useCallback((
    action: string,
    options?: { message?: string; details?: string; counts?: { success?: number; failed?: number; total?: number } }
  ): string => {
    const successMessage: SuccessMessage = options?.message
      ? { title: 'Sukces', message: options.message, details: options.details }
      : createSuccessMessage(action, options?.counts);

    return showNotification({
      type: 'success',
      title: successMessage.title,
      message: successMessage.message,
      details: successMessage.details || options?.details,
      autoClose: true,
    });
  }, [showNotification]);

  const showError = useCallback((error: unknown, context?: string): string => {
    const translated: TranslatedError = translateError(error, context);

    return showNotification({
      type: 'error',
      title: translated.title,
      message: translated.message,
      details: translated.details,
      originalError: translated.originalError,
      autoClose: false,
    });
  }, [showNotification]);

  const showWarning = useCallback((title: string, message: string, details?: string): string => {
    return showNotification({
      type: 'warning',
      title,
      message,
      details,
      autoClose: false,
    });
  }, [showNotification]);

  const showInfo = useCallback((title: string, message: string, details?: string): string => {
    return showNotification({
      type: 'info',
      title,
      message,
      details,
      autoClose: true,
    });
  }, [showNotification]);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
